import React, { useEffect, useRef, useState } from 'react';
import { View, Platform } from 'react-native';
// We need to require it conditionally or just import assuming it is available in web bundler
import { Html5Qrcode } from 'html5-qrcode';

// Nep apparaat hook
export const useCameraDevice = (position) => {
    return {
        id: position === 'front' ? 'user' : 'environment',
        position: position || 'back',
        supportsVideoHDR: false,
        supportsPhotoHDR: false,
        formats: [],
        minZoom: 1,
        maxZoom: 1,
    };
};

// Nep code scanner hook - geeft gewoon config door
export const useCodeScanner = (config) => {
    return config;
};

// Camera Component
export const Camera = (props) => {
    const { device, isActive, codeScanner, style } = props;
    const [elementId] = useState(`camera-reader-${Math.random().toString(36).substr(2, 9)}`);
    const scannerRef = useRef(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (scannerRef.current) {
                // Attempt cleanup
                try {
                    if (scannerRef.current.isScanning) {
                        scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(e => console.warn(e));
                    } else {
                        scannerRef.current.clear();
                    }
                } catch (e) {
                    console.warn(e);
                }
                scannerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!isActive) {
            if (scannerRef.current) {
                scannerRef.current.stop().then(() => {
                    scannerRef.current.clear();
                }).catch(e => { });
            }
            return;
        }

        // If active, ensure we start scanning
        const startScanner = async () => {
            const element = document.getElementById(elementId);
            if (!element) {
                // Retrying if DOM not ready
                setTimeout(startScanner, 100);
                return;
            }

            if (scannerRef.current && scannerRef.current.isScanning) return;

            // Create instance if not exists
            if (!scannerRef.current) {
                // Verbose mode helpful for debugging
                scannerRef.current = new Html5Qrcode(elementId, { verbose: false });
            }

            const config = {
                fps: 15, // Higher FPS for responsive scanning
                qrbox: { width: 280, height: 280 }, // Match UI frame
                // aspectRatio: 1.0, // Removed to allow full video feed
                experimentalFeatures: {
                    useBarCodeDetectorIfSupported: true
                },
                videoConstraints: {
                    facingMode: device?.position === 'front' ? "user" : "environment"
                }
            };

            const facingMode = device?.position === 'front' ? "user" : "environment";

            try {
                // Check if already scanning/started to avoid error
                if (scannerRef.current.isScanning) return;

                await scannerRef.current.start(
                    { facingMode: facingMode },
                    config,
                    (decodedText, decodedResult) => {
                        if (codeScanner && codeScanner.onCodeScanned) {
                            const code = {
                                type: 'qr', // Default
                                value: decodedText,
                                frame: undefined
                            };

                            // Try to map format name
                            if (decodedResult?.result?.format?.formatName) {
                                // e.g. EAN_13 -> ean-13
                                code.type = decodedResult.result.format.formatName.toLowerCase().replace('_', '-');
                            } else {
                                // Heuristic for numeric barcodes if format is missing
                                if (/^\d{13}$/.test(decodedText)) code.type = 'ean-13';
                                else if (/^\d{12}$/.test(decodedText)) code.type = 'upc-a';
                                else if (/^\d{8}$/.test(decodedText)) code.type = 'ean-8';
                            }

                            codeScanner.onCodeScanned([code]);
                        }
                    },
                    (errorMessage) => {
                        // ignore
                    }
                );
            } catch (err) {
                console.error("Fout bij starten web scanner", err);
            }
        };

        startScanner();

    }, [isActive, device, codeScanner, elementId]);

    return (
        <View
            style={[{ backgroundColor: 'black', overflow: 'hidden' }, style]}
            nativeID={elementId}
        />
    );
};

// Statische methodes voor compatibiliteit
Camera.requestCameraPermission = async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return 'denied';
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        // Stop the tracks immediately after getting permission
        stream.getTracks().forEach(t => t.stop());
        return 'granted';
    } catch (e) {
        console.warn("Camera toestemming fout", e);
        return 'denied';
    }
};

Camera.getCameraPermissionStatus = async () => {
    // Simplification: assume if we are on web, we might have it or need to ask. 
    // 'not-determined' triggers the request flow in most apps.
    // If we can check:
    if (typeof navigator !== 'undefined' && navigator.permissions) {
        try {
            const status = await navigator.permissions.query({ name: 'camera' });
            if (status.state === 'granted') return 'granted';
            if (status.state === 'denied') return 'denied';
        } catch (e) { }
    }
    return 'not-determined';
};

Camera.getAvailableCameraDevices = async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return [];
    }
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices
            .filter(d => d.kind === 'videoinput')
            .map(d => ({
                id: d.deviceId,
                position: d.label.toLowerCase().includes('front') ? 'front' : 'back',
                isMultiCam: false,
                minZoom: 1,
                maxZoom: 1,
                formats: []
            }));
    } catch (e) {
        return [];
    }
};

export const sortFormats = () => [];
export const frameRateIncluded = () => false;

export default {
    Camera,
    useCameraDevice,
    useCodeScanner,
    sortFormats,
    frameRateIncluded
};
